/* The generated tokens, then the app's own layout. Nothing below redefines a token:
   if a value is needed that is not here, it belongs in packages/tokens. */
@import '@acrostic/tokens/css';

/* ── CORNERS ─────────────────────────────────────────────────────────────────
   THE DESIGN FILE BINDS RADIUS VARIABLES NOW, so these moved to packages/tokens on
   2026-09-18, exactly as the comment that used to sit here said they should. What is
   left is five old names pointing at the three real ones, so 33 usages did not all
   have to be edited in one commit. Use --radius-md / --radius-lg / --radius-full in
   anything new; these five exist to be swept away.

   THE STEP-DOWN DOCTRINE IS GONE, AND THAT IS A REVERSAL WORTH NAMING. The old scale
   had five values on the argument that nested shapes must never share a radius: a
   card inside a panel took the next value down, and the board column and its cards
   were tuned to be properly concentric (8 + 6 = 14). Acrostic_Design-System does not
   work that way. It has two radii and assigns them by ROLE rather than by depth: a
   surface is 12, a control is 8. Its own Kanban is a 12px card inside an 8px column,
   which is the inner shape being ROUNDER than the outer one and the exact thing the
   old rule forbade.

   The design file wins, because a scale that disagrees with the design file is a
   scale nobody can check a screen against. The concentric tuning is not preserved.
   If the step-down reads better once it is on screen, that is a conversation to have
   with the design file rather than a fifth value quietly reintroduced here.

   The mapping, by role and not by nearest number:
     surface (panel, dialog, modal)       14 -> 12   --radius-lg
     control (button, field, cell, tile)  10 ->  8   --radius-md
     control                               8 ->  8   --radius-md, unchanged
     control inside a card                 6 ->  8   --radius-md
     pill                                999 -> 999  --radius-full                 */
:root {
  --r-1: var(--radius-lg);      /* DEPRECATED: a surface. Use --radius-lg.         */
  --r-2: var(--radius-md);      /* DEPRECATED: mostly controls. Use --radius-md.   */
  --r-3: var(--radius-md);      /* DEPRECATED: a control. Use --radius-md.         */
  --r-4: var(--radius-md);      /* DEPRECATED: a control. Use --radius-md.         */
  --r-pill: var(--radius-full); /* DEPRECATED: use --radius-full.                  */
}

*, *::before, *::after { box-sizing: border-box; }

html, body, #root { height: 100%; }

body {
  margin: 0;
  font-family: var(--font-sans);
  /* Body Medium is the base role. Every other size on the page is a .t-* class, so a
     component never names a pixel. */
  font-size: var(--t-link-medium-size);
  line-height: var(--t-link-medium-line);
  color: var(--color-text-body);
  background: var(--color-surface-page);
  -webkit-font-smoothing: antialiased;
}

h1, h2, h3, h4, p, figure { margin: 0; }

/* THE PAGE DOES NOT SCROLL. The content pane does.
   `.main` was missing `min-height: 0`, so it could not shrink below its content and
   `.content`'s overflow never engaged: the whole document scrolled instead, which
   dragged the sidebar's identity block to the bottom of a two-thousand-pixel page
   rather than leaving it in the corner of the screen where it belongs. */
.shell { display: flex; height: 100%; }

.sidebar {
  width: 232px; flex: none;
  background: var(--color-surface-on-surface); color: var(--color-text-body);
  display: flex; flex-direction: column; gap: var(--s5, 20px);
  padding: 20px 16px;
  /* Its own scroller, for a seat whose nav is longer than the screen. The identity
     block below the spacer therefore sits at the bottom of the VIEWPORT. */
  height: 100%; overflow-y: auto;
  position: sticky; top: 0;
}
.sidebar .brand { color: var(--color-text-body); font-weight: 700; letter-spacing: -0.5px; }
.sidebar nav { display: flex; flex-direction: column; gap: 2px; margin-top: 8px; }
.sidebar nav a {
  color: var(--color-text-body); text-decoration: none;
  padding: 7px 10px; border-radius: var(--radius-md);
}
/* THE CURRENT ITEM IS A LIGHT PILL WITH DARK TEXT, which is the design file's side
   navigation and a reversal of what was here. The old treatment was a barely-lighter
   black (#17171a): on a black sidebar that is a 1.1:1 difference, so the only thing
   marking the current page was a text colour going from grey to near-white. The pill
   inverts instead, which is legible at a glance and is what the design shows. */
.sidebar nav a:hover {
  background: var(--color-surface-action-hover-transparent);
  color: var(--color-text-on-surface);
}
.sidebar nav a[aria-current='page'] {
  background: var(--color-surface-action-hover-transparent);
  color: var(--color-text-on-surface);
}

.main { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; }
.topbar {
  height: 56px; flex: none; display: flex; align-items: center; gap: 16px;
  padding: 0 20px; background: var(--surface);
  border-bottom: 1px solid var(--color-border-on-surface);
}
.topbar .spacer { flex: 1; }
.content { flex: 1; min-height: 0; overflow-y: auto; padding: 20px; }
.page { max-width: 1200px; margin: 0 auto; display: grid; gap: 16px; }

.panel {
  background: var(--surface); border: 1px solid var(--color-border-on-surface);
  border-radius: var(--r-1); padding: 16px;
}
/* THE DESIGN FILE'S CARD HEADER: the title with its subtitle stacked underneath, and
   a rule between the header and the body. The rule bleeds to the card's edge by
   cancelling the panel's own padding, which is how the design draws it - the header
   is a band across the top of the card rather than the first thing inside its
   padding. Its padding is the design's: 20 across, 8 down.

   A header that has no .htitle child keeps the old inline behaviour, so the panels
   that build their own header markup are untouched. */
.panel > header {
  display: flex; align-items: baseline; gap: 10px; margin-bottom: 12px; flex-wrap: wrap;
}
.panel > header:has(.htitle) {
  align-items: center; flex-wrap: nowrap;
  margin: calc(var(--space-4) * -1) calc(var(--space-4) * -1) var(--space-4);
  padding: var(--space-2) var(--space-5);
  border-bottom: var(--border-thin) solid var(--color-border-on-surface);
}
/* The stacked pair. It takes the slack so anything on the right keeps its place. */
.panel > header .htitle { display: grid; gap: 1px; min-width: 0; flex: 1; }
/* NOT UPPERCASE IN HERE. The eyebrow elsewhere is a label above a thing; this is a
   sentence under a title, and the design sets it as plain Caption. */
.panel > header .htitle .eyebrow {
  text-transform: none; letter-spacing: 0; color: var(--color-text-placeholder);
}
/* The title never shrinks to make room for its own eyebrow, and a link on the right
   never wraps mid-phrase: both looked like rendering faults on the consoles. */
.panel > header > h2 { flex: none; }
.panel > header > a, .panel > header > .linkish { flex: none; white-space: nowrap; }
/* The eyebrow TAKES the slack and wraps inside itself. Given flex:1 it pushed the
   link on the right onto its own line, which read as a stray link under a heading. */
.panel > header .eyebrow { flex: 1 1 140px; min-width: 0; }
.panel > header .spacer { flex: 0 1 auto; }
.eyebrow {
  text-transform: uppercase; letter-spacing: .08em;
  color: var(--text-muted);
}

/* A row is a person. The name is the thing, so it is the only full-contrast text. */
.rows { display: grid; }
.row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 4px; border-top: 1px solid var(--color-border-on-surface);
}
.rows .row:first-child { border-top: 0; }
.row .who { min-width: 0; flex: 1; }
.row .who .n { color: var(--color-text-heading); }
.row .who .s { color: var(--ink-2); }
.none { color: var(--text-muted); padding: 10px 4px; }

.score { font-variant-numeric: tabular-nums; color: var(--color-text-heading); }
.dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.dot.risk { background: var(--risk-fill); }
.dot.warn { background: var(--warn-fill); }
.dot.good { background: var(--good-fill); }

/* ---- sign in ---- */
.signin { min-height: 100%; display: grid; place-items: center; padding: 24px; }
.signin .card {
  width: 100%; max-width: 420px; background: var(--surface);
  border: 1px solid var(--color-border-on-surface); border-radius: var(--r-1); padding: 28px;
}
.signin h1 { color: var(--color-text-heading); }
.seats { display: grid; gap: 8px; margin-top: 16px; }
/* Scoped to the sign-in card. A bare `.seat` is too generic a name for a styled
   button: it has now twice been picked up by markup that meant the word literally.
   Scoping costs nothing and makes the next collision impossible. */
.signin .seat {
  display: flex; flex-direction: column; gap: 2px; text-align: left;
  padding: 12px 14px; border-radius: var(--r-2); cursor: pointer;
  background: var(--surface); border: 1px solid var(--color-border-on-surface);
  font-family: inherit; color: var(--color-text-heading);
}
.signin .seat:hover:not(:disabled) { border-color: var(--color-text-heading); background: var(--surface-2); }
.signin .seat:disabled { cursor: default; opacity: .6; }

.linkish {
  background: none; border: 0; padding: 0; cursor: pointer;
  font-family: inherit; color: var(--ink-2); text-decoration: underline;
}
.linkish:hover { color: var(--color-text-heading); }

/* ---- nav groups, stats, and the rest of the shell ---- */
.navhead {
  text-transform: uppercase; letter-spacing: .08em;
  color: var(--color-text-placeholder); padding: 12px 10px 4px;
}
/* .whoami, not .seat: .seat is the sign-in button, and one class name for two
   different things is a selector that will eventually pick the wrong one. */
/* Pinned to the bottom of the sidebar's scroll box, not to the end of its content:
   a nav long enough to scroll must not carry the identity out of sight with it.
   Sign out sits in it, beside the name it signs out of. */
.sidebar .whoami {
  position: sticky; bottom: 0; margin-top: auto;
  background: var(--color-surface-on-surface);
  border-top: 1px solid var(--color-border-on-surface);
  padding-top: 12px;
  display: flex; align-items: center; gap: 10px;
}
/* The three identity lines truncate rather than wrap. A long gym name must not
   push the control it sits beside onto another row or off the edge. */
.sidebar .whoami .who { min-width: 0; flex: 1; }
.sidebar .whoami .who > * {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sidebar .whoami .gym { color: var(--color-text-body); }
.sidebar .whoami .signout {
  flex: none; font-family: inherit; cursor: pointer;
  background: none; color: var(--color-text-placeholder);
  border: 1px solid var(--color-border-on-surface); border-radius: var(--r-3);
  padding: 5px 10px;
}
.sidebar .whoami .signout:hover {
  color: var(--color-text-on-surface);
  border-color: var(--color-surface-action-hover-transparent);
  background: var(--color-surface-action-hover-transparent);
}
/* TEXT-MUTED, NOT FIGMA'S PLACEHOLDER. Figma's is 4.20:1 on white, under the 4.5:1
   bar for body text - right for an input placeholder, wrong for the captions and
   empty states that were all reading from it. `.field::placeholder` keeps Figma's. */
.muted { color: var(--text-muted); }

.stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 16px; }
.stat .v { color: var(--color-text-heading); }
.stat .l { color: var(--text-muted); }
.stat .linkish { margin-top: 6px; display: inline-block; }

/* A row that opens a record is a button, and has to stop looking like one. */
button.row {
  width: 100%; text-align: left; font-family: inherit; font-size: inherit;
  background: none; border: 0; border-top: 1px solid var(--color-border-on-surface);
  cursor: pointer;
}
.rows button.row:first-child { border-top: 0; }
button.row:hover { background: var(--surface-2); }

/* ---- lists ---- */
.field {
  font-family: inherit; padding: 6px 10px; min-width: 240px;
  border: 1px solid var(--color-border-on-surface); border-radius: var(--r-3);
  background: var(--surface); color: var(--color-text-body);
}
.field::placeholder { color: var(--color-text-placeholder); }

.filters { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.filters .spacer { flex: 1; }
.filters select {
  font-family: inherit; border: 1px solid var(--color-border-on-surface);
  border-radius: var(--r-3); padding: 4px 6px; background: var(--surface);
  color: var(--color-text-heading);
}
.seg { display: inline-flex; border: 1px solid var(--color-border-on-surface); border-radius: var(--r-3); overflow: hidden; }
.segbtn {
  font-family: inherit; padding: 6px 12px; cursor: pointer;
  background: var(--surface); border: 0; border-left: 1px solid var(--color-border-on-surface);
  color: var(--ink-2);
}
.seg .segbtn:first-child { border-left: 0; }
.segbtn.on { background: var(--signal); color: var(--color-text-on-surface); }

.table { width: 100%; border-collapse: collapse; }
.table th {
  text-align: left; padding: 8px 6px; color: var(--text-muted);
  border-bottom: 1px solid var(--color-border-on-surface); font-weight: 500;
}
.table th.right, .table td.right { text-align: right; }
.table td { padding: 10px 6px; border-bottom: 1px solid var(--color-border-on-surface); }
.table tbody tr { cursor: pointer; }
.table tbody tr:hover { background: var(--surface-2); }
/* THE FLEX IS FOR AN AVATAR BESIDE A NAME, and only for a cell that has one.
   It used to be on every first cell, and `display: flex` stops a td being a
   table-cell: `vertical-align: middle` no longer applies to it and it sizes to its own
   content rather than the row's. On the Classes schedule, whose first cell is just a
   time, that left the time sitting 7px above the class name beside it - the row's
   other cells were 55px tall and it was 42.

   The follow-up table had already hit this and undone it for itself
   (`.table.fu td:first-child` below). That override still wins where it applies; this
   fixes the cause so the next table does not need its own. */
.table td:first-child { color: var(--color-text-heading); }
.table td:first-child:has(.avatar) { display: flex; align-items: center; gap: 8px; }

.pager { display: flex; align-items: center; gap: 12px; margin-top: 12px; }
.btn {
  font-family: inherit; padding: 6px 12px; border-radius: var(--r-3); cursor: pointer;
  border: 1px solid var(--color-border-on-surface); background: var(--surface);
  color: var(--color-text-heading);
}
.btn:disabled { opacity: .45; cursor: default; }

/* ---- the member record ---- */
.record-head { display: flex; gap: 18px; align-items: flex-start; }
.record-head .face {
  width: 64px; height: 64px; border-radius: 50%; flex: none;
  display: grid; place-items: center; background: var(--signal-soft);
  color: var(--signal-ink); font-weight: 600; font-size: 20px;
}
.record-head .idblock { flex: 1; min-width: 0; }
.record-head .idblock h2 { color: var(--color-text-heading); }
.record-head .scoreblock { text-align: right; flex: none; }
.bigscore.risk { color: var(--risk); }
.bigscore.warn { color: var(--warn); }
.bigscore.good { color: var(--good); }

/* THE MOVE, under the score. It is a sentence, not a badge: a badge beside a number
   reads as a second number. Held to a column so it wraps into the score block rather
   than stretching the header, and left-aligned inside a right-aligned block because
   ragged-right prose is what a reader expects. */
/* The row above the record: back on the left, Edit on the right. */
.recordbar { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.recordbar .spacer { flex: 1; }

.record-head .guidance {
  max-width: 24ch; margin-top: 6px; margin-left: auto;
  text-align: left; color: var(--ink-2);
}

.chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.chip {
  padding: 2px 9px; border-radius: var(--r-pill); background: var(--surface-2);
  color: var(--ink-2); border: 1px solid var(--color-border-on-surface);
}

.tabstrip { display: flex; flex-wrap: wrap; gap: 2px; border-bottom: 1px solid var(--color-border-on-surface); }
.tab {
  font-family: inherit; background: none; border: 0; cursor: pointer;
  padding: 9px 12px; color: var(--ink-2); border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}
.tab:hover { color: var(--color-text-heading); }
.tab.on { color: var(--color-text-heading); border-bottom-color: var(--signal); }

.facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 14px; }
.fact .t-label-medium { color: var(--color-text-heading); }
.whylist { margin: 12px 0 0; padding-left: 18px; color: var(--ink-2); }

.fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px 20px; }
.fieldrow { border-bottom: 1px solid var(--color-border-on-surface); padding-bottom: 6px; }

.timeline { list-style: none; margin: 0; padding: 0; }
.tl { display: flex; gap: 14px; padding: 10px 0; border-top: 1px solid var(--color-border-on-surface); }
.timeline .tl:first-child { border-top: 0; }
.tl .when { width: 84px; flex: none; padding-top: 2px; }
.tl .what { min-width: 0; }
.tl .what .t-label-large { color: var(--color-text-heading); }

/* ---- faces ---- */
/* THE RING IS THE DESIGN FILE'S, and it is border/default rather than the hairline:
   a face is a component whose edge IS the component, so it takes the border that
   clears 3:1 instead of the one that separates panels. It also stops a light-topped
   photo dissolving into a white card. */
.avatar {
  border-radius: var(--radius-full); flex: none; object-fit: cover;
  display: inline-grid; place-items: center;
  background: var(--color-surface-page); color: var(--signal-ink);
  border: var(--border-thin) solid var(--color-border-default);
  font-weight: 600; overflow: hidden;
}
.avatar.sm { width: 28px; height: 28px; font-size: 11px; }
.avatar.md { width: 36px; height: 36px; font-size: 13px; }
.avatar.lg { width: 64px; height: 64px; font-size: 20px; }

/* ---- badges ----
   A badge is a state, not decoration, so the palette is the same four tones the
   dots use. `ahead` is a stage nobody has reached yet: present, and quiet. */
.badge {
  display: inline-block; padding: 2px 9px; border-radius: var(--r-pill);
  background: var(--surface-2); color: var(--ink-2);
  border: 1px solid var(--color-border-on-surface); white-space: nowrap;
}
.badge.good { background: var(--good-soft); color: var(--good); border-color: transparent; }
.badge.warn { background: var(--warn-soft); color: var(--warn); border-color: transparent; }
.badge.risk { background: var(--risk-soft); color: var(--risk); border-color: transparent; }
.badge.info { background: var(--info-soft); color: var(--info); border-color: transparent; }
.badge.ahead { opacity: .45; }

/* ---- the board ---- */
.kanban {
  display: grid; grid-auto-flow: column; grid-auto-columns: minmax(212px, 1fr);
  gap: 12px; overflow-x: auto; padding-bottom: 6px; align-items: start;
}
.kcol {
  background: var(--color-surface-page); border: 1px solid var(--color-border-on-surface);
  /* THE COLUMN IS THE SMALLER RADIUS AND THE CARDS INSIDE IT ARE THE LARGER ONE.
     That is the design file's Kanban, not a slip: it assigns radius by role, and a
     column is a container (md) while a lead is a card (lg). It reverses the
     concentric tuning that used to be here. See the CORNERS block at the top. */
  border-radius: var(--radius-md); padding: 6px; display: grid; gap: 6px; align-content: start;
  min-height: 120px;
}
.kcol.dragover { border-color: var(--signal); background: var(--signal-soft); }
.khead {
  display: flex; align-items: baseline; gap: 8px;
  color: var(--color-text-heading); padding: 6px 8px 8px;
}
.khead .kcount { margin-left: auto; color: var(--text-muted); font-variant-numeric: tabular-nums; }
.kcard {
  display: grid; gap: 4px; text-align: left; width: 100%; cursor: pointer;
  font-family: inherit; font-size: inherit;
  background: var(--color-surface-on-surface); border: 1px solid var(--color-border-on-surface);
  /* The design file's Lead card: a card, so the surface radius. */
  border-radius: var(--radius-lg); padding: var(--space-2);
}
.kcard:hover { border-color: var(--color-text-heading); }
.kcard .kh { display: flex; align-items: center; gap: 8px; min-width: 0; }
.kcard .kn { color: var(--color-text-heading); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.kcard .kt { display: flex; gap: 6px; margin-top: 4px; }
.kmore-wrap { display: grid; gap: 4px; justify-items: start; padding: 4px 4px 2px; }

/* ---- two-column cards ---- */
/* TWO COLUMNS, never three. A third column makes every card narrower than the
   sentences inside it, and a record is read a card at a time rather than scanned. */
.grid2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; align-items: start; }
@media (max-width: 760px) { .grid2 { grid-template-columns: 1fr; } }
.deflist { display: grid; }
.defrow {
  display: flex; gap: 12px; align-items: baseline;
  padding: 6px 0; border-bottom: 1px solid var(--color-border-on-surface);
}
.defrow > :first-child { min-width: 150px; flex: none; }
.defrow > :last-child { color: var(--color-text-heading); font-weight: 500; }
.stagepath { display: flex; gap: 6px; flex-wrap: wrap; }

/* ---- the lead record ---- */
.lead-head { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(260px, 1fr); gap: 16px; align-items: start; }
@media (max-width: 900px) { .lead-head { grid-template-columns: 1fr; } }
.record-head .titlerow { display: flex; flex-wrap: wrap; gap: 8px; align-items: baseline; }
.record-head .actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
.converted { background: var(--signal-soft); border-color: transparent; }
.converted .t-label-large { color: var(--signal-ink); }

/* ---- the cadence strip ----
   Pills where they fit, ticks where they do not. Both carry the same title, so the
   dense strip loses the label and not the information. */
.track .dots { display: flex; gap: 5px; flex-wrap: wrap; align-items: center; }
.track .dots.dense { gap: 3px; }
.cp {
  min-width: 26px; padding: 2px 6px; border-radius: var(--r-pill); text-align: center;
  font-size: var(--t-label-xsmall-size); line-height: 18px;
  background: var(--surface-2); color: var(--text-muted);
  border: 1px solid var(--color-border-on-surface);
}
.tick { width: 8px; height: 8px; border-radius: 2px; background: var(--color-border-on-surface); }
.cp.done { background: var(--good-soft); color: var(--good); border-color: transparent; }
.tick.done { background: var(--good-fill); }
.cp.late { background: var(--risk-soft); color: var(--risk); border-color: transparent; }
.tick.late { background: var(--risk-fill); }
.cp.now { border-color: var(--signal); color: var(--color-text-heading); }
.tick.now { background: var(--signal); }
.meter { height: 6px; border-radius: var(--r-pill); background: var(--surface-2); margin-top: 10px; overflow: hidden; }
.meter i { display: block; height: 100%; }

/* ---- modal ---- */
.modal {
  border: 1px solid var(--color-border-on-surface); border-radius: var(--r-1); padding: 0;
  width: min(560px, calc(100vw - 32px)); background: var(--surface);
  color: var(--color-text-body); font-family: inherit;
}
.modal::backdrop { background: rgba(11, 11, 12, .45); }
.modal header { display: flex; align-items: baseline; gap: 12px; padding: 16px 18px 0; }
.modal header .spacer { flex: 1; }
.modal header h2 { color: var(--color-text-heading); }
.modal .modal-body { padding: 14px 18px; display: grid; gap: 12px; }
.modal footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 18px 16px; border-top: 1px solid var(--color-border-on-surface);
}
/* TWO FIELDS ON A ROW, and one column the moment there is not room. minmax(0,1fr)
   rather than 1fr: a grid track's default min-width is auto, so a long value in a
   text input pushes the track wider than the dialog instead of scrolling inside it. */
.formrow {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px; align-items: end;
}
@media (max-width: 560px) { .formrow { grid-template-columns: 1fr; } }

/* The block a field opens up — the suspension period under the status select. Set
   in from the form so it reads as part of the answer above it, not a new question. */
.subform {
  display: grid; gap: 10px; margin: 4px 0 2px;
  padding: 12px; border-radius: var(--r-md, 8px);
  background: var(--surface-2); border: 1px solid var(--color-border-on-surface);
}
.chiprow { display: flex; flex-wrap: wrap; gap: 6px; }

/* Said before the button is pressed, not after: this change is going for sign-off. */
.notice {
  margin-bottom: 4px; padding: 10px 12px;
  border-radius: var(--r-md, 8px);
  background: var(--surface-2); border: 1px solid var(--color-border-on-surface);
  color: var(--ink-2);
}

.addnote { display: grid; gap: 8px; margin-bottom: 12px; }

.formfield { display: grid; gap: 4px; }
.formfield > .t-label-small { color: var(--color-text-heading); }
.formfield .field { min-width: 0; width: 100%; }
.btn.primary { background: var(--signal); color: var(--color-text-on-surface); border-color: var(--signal); }
.btn.primary:disabled { opacity: .45; }
textarea.field { resize: vertical; font-size: var(--t-link-small-size); line-height: var(--t-link-small-line); }

/* ---- toasts ----
   Bottom centre, over everything including an open dialog: a save that happened
   inside a modal has to be reported where the modal is. */
.toasts {
  position: fixed; inset: auto 0 24px; z-index: 10;
  display: grid; gap: 8px; justify-items: center; pointer-events: none;
  /* A popover comes with a border, a background and padding of its own, and it is
     display:none until shown. The host is only a positioner: everything visible is
     the .toast inside it. */
  border: 0; background: none; padding: 0; margin: 0; width: 100%; max-width: none;
  overflow: visible;
}
.toasts:popover-open { display: grid; }
.toast {
  background: var(--color-surface-on-surface); color: var(--color-text-body);
  padding: 8px 14px; border-radius: var(--r-pill); max-width: min(520px, 90vw);
}
.toast.good { background: var(--good); }
.toast.bad { background: var(--risk); }

/* ---- the day strip ---- */
.daystrip { display: flex; gap: 6px; flex-wrap: wrap; }
.day {
  display: grid; gap: 0; justify-items: center; min-width: 62px;
  font-family: inherit; cursor: pointer; padding: 6px 10px; border-radius: var(--r-2);
  background: var(--surface); border: 1px solid var(--color-border-on-surface);
  color: var(--ink-2);
}
.day .dn { color: var(--color-text-heading); }
.day.on { background: var(--signal); border-color: var(--signal); }
.day.on .dw, .day.on .dn { color: var(--color-text-on-surface); }

/* ---- the context bar ----
   A count is allowed here only because it opens its names, and the first three of
   those names are printed under it, so the bar is never only a number. */
.ctx-bar .ctx-stat {
  text-align: left; font-family: inherit; cursor: pointer;
  background: var(--surface); border: 1px solid var(--color-border-on-surface);
  border-radius: var(--r-2); padding: 12px; display: grid; gap: 2px; align-content: start;
}
.ctx-bar .ctx-stat:hover:not(:disabled) { border-color: var(--color-text-heading); }
.ctx-bar .ctx-stat.off { cursor: default; }
.ctx-bar .ctx-stat .v { color: var(--color-text-heading); }
.names { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; align-items: center; }

/* ---- pills: a fact beside a name ----
   `flat` is the fact with the colour taken out: what suppression leaves behind. */
/* THE DESIGN FILE CALLS THIS A STATUS BADGE and gives it a visible edge in its own
   tone: error-subtle fill, error border, error text. The tones here used to set
   border-color: transparent, which made a tinted lozenge rather than a badge. The
   border is what lets it sit on a white card and still read as a state. */
.pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 1px var(--space-2); border-radius: var(--radius-full); white-space: nowrap;
  background: var(--color-surface-page); color: var(--ink-2);
  border: var(--border-thin) solid var(--color-border-on-surface);
}
.pill.good { background: var(--good-soft); color: var(--good); border-color: var(--color-border-success); }
.pill.warn { background: var(--warn-soft); color: var(--warn); border-color: var(--color-border-warning); }
.pill.risk { background: var(--risk-soft); color: var(--risk); border-color: var(--color-border-error); }
.pill.flat { background: var(--surface-2); color: var(--text-muted); }
/* The score as a badge. Tabular so a column of them does not jitter, and the tone
   comes from the same three classes above, so the number and its colour cannot
   disagree the way a separate dot beside a plain number could. */
.pill.score { font-variant-numeric: tabular-nums; min-width: 34px; justify-content: center; }
.pill.face { padding-left: 2px; }
.pill .avatar.sm { width: 20px; height: 20px; font-size: 9px; }
.row .n .pill { margin-left: 6px; }

/* ---- the register ---- */
.register { max-height: min(60vh, 520px); overflow-y: auto; }
.cxentry { border-top: 1px solid var(--color-border-on-surface); }
.register .cxentry:first-child { border-top: 0; }
.cxrow { display: flex; align-items: flex-start; gap: 6px; border-radius: var(--r-4); }
.cxrow.on { background: var(--good-soft); }
.cxrow .row { flex: 1; min-width: 0; border-top: 0; cursor: pointer; align-items: flex-start; background: none; }
.cbx { width: 18px; height: 18px; flex: none; margin-top: 8px; accent-color: var(--signal); }
.ctx { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.ctx-btn {
  font-family: inherit; cursor: pointer; align-self: center;
  padding: 4px 10px; border-radius: var(--r-3);
  background: var(--surface); border: 1px solid var(--color-border-on-surface);
  color: var(--ink-2);
}
.ctx-btn.on { background: var(--signal); color: var(--color-text-on-surface); border-color: var(--signal); }
.go {
  font-family: inherit; cursor: pointer; align-self: center; flex: none;
  background: none; border: 0; color: var(--text-muted); padding: 4px 6px;
}
.go:hover { color: var(--color-text-heading); }
.ctx-note { display: grid; gap: 8px; padding: 0 0 12px 24px; }
.ctx-acts { display: flex; gap: 8px; align-items: center; }

/* ---- segments ---- */
.segcard {
  display: grid; gap: 4px; text-align: left; cursor: pointer;
  font-family: inherit; align-content: start;
}
.segcard:hover { border-color: var(--color-text-heading); }
.segcard h3 { color: var(--color-text-heading); }
.segcard .t-display-xsmall { color: var(--color-text-heading); }

/* ---- toggles and notices ---- */
.togglerow {
  display: flex; gap: 12px; align-items: flex-start; cursor: pointer;
  padding: 12px 0; border-bottom: 1px solid var(--color-border-on-surface);
}
.togglerow input { width: 18px; height: 18px; flex: none; margin-top: 3px; accent-color: var(--signal); }
.togglerow > span { display: grid; gap: 2px; }
.togglerow .t-label-large { color: var(--color-text-heading); }
.notice {
  padding: 10px 12px; border-radius: var(--r-3); margin-bottom: 10px;
  background: var(--surface-2); color: var(--ink-2);
}
.notice.warn { background: var(--warn-soft); color: var(--warn); }
.pw {
  width: 100%; text-align: center; letter-spacing: .5px; font-weight: 700;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* A table cell that carries a face, a name and a line under it. */
.namecell { display: grid; gap: 1px; min-width: 0; }
.namecell .t-label-large { color: var(--color-text-heading); }
.table td .badge + .badge, .table td .t-label-large .badge { margin-left: 6px; }

/* ---- the console ---- */
.toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.toolbar .spacer { flex: 1; }

/* A row that carries actions gets a second line rather than squeezing them beside
   the score: .row does not wrap, so it opts in. */
.row.stacked { align-items: flex-start; }
.rowacts { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
.namelink {
  background: none; border: 0; padding: 0; cursor: pointer; text-align: left;
  font: inherit; color: var(--color-text-heading);
}
.namelink:hover { text-decoration: underline; }

/* ---- the lifecycle strip ----
   The arrows are a READING ORDER, not a flow: the stages overlap. */
.lcstrip .lchead { display: flex; gap: 16px; align-items: flex-start; margin-bottom: 12px; }
.lcstrip .lchead h2 { color: var(--color-text-heading); }
.lcstrip .lcnote { max-width: 380px; margin-left: auto; text-align: right; }
.lcrow { display: flex; align-items: stretch; gap: 0; overflow-x: auto; }
.lcpair { display: flex; align-items: center; flex: 1; min-width: 0; }
.lcarrow { color: var(--color-border-on-surface); padding: 0 4px; flex: none; }
.lccell {
  flex: 1; min-width: 84px; display: grid; gap: 1px; justify-items: center;
  font-family: inherit; cursor: pointer; padding: 10px 6px; border-radius: var(--r-2);
  background: none; border: 1px solid transparent;
}
.lccell:hover:not(:disabled) { background: var(--surface-2); border-color: var(--color-border-on-surface); }
.lccell:disabled { cursor: default; }
.lccell .lcn { color: var(--color-text-heading); font-variant-numeric: tabular-nums; }
.lccell.risk .lcn { color: var(--risk); }
.lccell .lcl { color: var(--ink-2); text-align: center; }
.lcd { color: var(--good); }
.lcd.down { color: var(--risk); }
.lcd.flat { color: var(--text-muted); }

/* ---- the follow-up table ---- */
.table.fu td { vertical-align: top; }
.table.fu td:first-child { display: table-cell; }
.table.fu .t-label-large { color: var(--color-text-heading); }

/* ---- tier mix ---- */
.tiers { display: flex; gap: 20px; }
.tier { display: flex; align-items: baseline; gap: 6px; }
.tier .t-label-large { color: var(--color-text-heading); font-variant-numeric: tabular-nums; }
.tierbar { display: flex; height: 8px; border-radius: var(--r-pill); overflow: hidden; margin-top: 12px; background: var(--surface-2); }
.tierbar i.risk { background: var(--risk-fill); }
.tierbar i.warn { background: var(--warn-fill); }
.tierbar i.good { background: var(--good-fill); }

/* ---- the walkthrough ----
   The veil dims everything and the spotlight is a hole in it, cut with a very large
   box-shadow rather than an SVG mask: one element, no clip path to keep in step with
   a moving target. */
.tour-veil { position: fixed; inset: 0; z-index: 40; background: transparent; }
.tour-veil.dim { background: rgba(11, 11, 12, .55); }
.tour-spot {
  position: fixed; z-index: 41; border-radius: var(--r-1); pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(11, 11, 12, .55);
  outline: 2px solid var(--surface); outline-offset: 0;
  transition: left .18s ease, top .18s ease, width .18s ease, height .18s ease;
}
.tour-card {
  position: fixed; z-index: 42; width: 380px; max-width: calc(100vw - 32px);
  background: var(--surface); border-radius: var(--r-1); padding: 16px 18px;
  box-shadow: 0 18px 48px rgba(11, 11, 12, .3);
  display: grid; gap: 6px;
}
.tour-card.center { left: 50%; top: 50%; transform: translate(-50%, -50%); }
.tour-card h3 { color: var(--color-text-heading); }
.tour-card p { color: var(--ink-2); }
.tour-card .foot { display: flex; align-items: center; gap: 8px; margin-top: 10px; }

.pill.walkthrough {
  cursor: pointer; font-family: inherit;
  background: var(--surface-2); border: 1px solid var(--color-border-on-surface);
  color: var(--ink-2); padding: 4px 12px;
}
.pill.walkthrough:hover { color: var(--color-text-heading); border-color: var(--color-text-heading); }

/* The walkthrough is a popover so it can point at something inside a dialog. The
   host is only a container: it has no box of its own. */
.tour {
  position: fixed; inset: 0; z-index: 40;
  border: 0; background: none; padding: 0; margin: 0;
  width: 100%; max-width: none; height: 100%; max-height: none; overflow: visible;
}
.tour:popover-open { display: block; }

/* ---- narrow ----
   A gym floor runs this on a tablet and an owner opens it on a phone. Below 860 the
   sidebar becomes a scrolling strip across the top: the nav is still every screen,
   it just reads as a row. Nothing is hidden behind a hamburger, because a seat with
   four items does not need one.

   The page itself must never scroll sideways. A table may, inside its own box. */
@media (max-width: 860px) {
  .shell { flex-direction: column; }
  .sidebar {
    width: auto; flex-direction: row; align-items: center; gap: 12px;
    padding: 10px 14px; overflow-x: auto;
  }
  .sidebar nav { flex-direction: row; margin-top: 0; gap: 4px; flex: 1; }
  .sidebar nav a { white-space: nowrap; padding: 6px 10px; }
  /* The group headings only make sense as headings above a column. */
  .sidebar .navhead { display: none; }
  .sidebar .brand { flex: none; }
  /* The identity stays, because Sign out is in it now. It loses the gym and the
     seat, which are the two lines a strip has no room for and the two a person
     signing out does not need. */
  .sidebar .whoami {
    position: static; margin-top: 0; padding-top: 0;
    border-top: 0; border-left: 1px solid var(--color-border-on-surface);
    padding-left: 12px; flex: none;
  }
  .sidebar .whoami .gym, .sidebar .whoami .seatline { display: none; }
  .sidebar .whoami .who { flex: none; max-width: 120px; }
  .content { padding: 14px; }
  .topbar { padding: 0 14px; }
}

/* A table is allowed to be wider than the screen. It scrolls inside the panel, and
   the panel does not. */
.panel { overflow-x: auto; }
.table { min-width: 520px; }
.lcrow { min-width: 0; }

/* ---- worth and value ----
   The row is marked when what they bring is worth more than what they pay. Not a
   badge and not a score: both numbers are already on the row and this only says
   which way round they are. */
.table.worth tr.brings-more { background: var(--good-soft); }
.table.worth tr.brings-more td:first-child { box-shadow: inset 3px 0 0 var(--good-fill); }
.table.worth td { vertical-align: middle; }

/* A toggle the reader may see and may not change. Not hidden: a control that is
   absent is a question they then have to ask somebody. */
.togglerow.locked { opacity: .6; cursor: default; }

/* THE LADDER STEP, on the action line and nowhere else (PRD 4).
   Quiet on purpose. It is a position in the gym's sequence of moves, so it reads
   as a label above the move rather than as a score beside the person. It is
   deliberately not a pill: a pill is what a Status Badge is, and a badge next to
   somebody is the exact reading - "a level of this member" - the numbering was
   taken off the record for in the first place. */
.scoreblock .step {
  color: var(--color-text-placeholder);
  text-transform: uppercase; letter-spacing: .08em;
  margin-top: var(--space-2);
}

/* Two layout nudges that used to be inline pixel values. They are here rather than
   in the markup because PRD 15 says tokens only and a screen naming a pixel is the
   thing the copy gate now refuses. Both sit on the space scale. */
.tight-above { margin: calc(var(--space-2) * -1) 0 0; }
.track-what { margin: var(--space-1) 0 var(--space-4); }


/* ---- the conversation thread (PRD 6) ----
   Inbound and outbound lean opposite ways, which is the one convention every
   messaging app shares and the cheapest way to make direction readable without a
   label on every line. The member's words sit on the page surface and the gym's on
   the action colour: the gym is the one doing the talking, and the thread should
   make that visible at a glance rather than flattering it. */
.thread { display: grid; gap: var(--space-3); padding: var(--space-2) 0; }
.msg { display: grid; gap: 2px; max-width: 78%; }
.msg.out { justify-self: end; justify-items: end; }
.msg.in { justify-self: start; justify-items: start; }
.msg .bubble {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-lg);
  border: var(--border-thin) solid var(--color-border-on-surface);
}
.msg.out .bubble {
  background: var(--color-surface-action); color: var(--color-text-on-surface);
  border-color: var(--color-border-action);
}
.msg.in .bubble { background: var(--color-surface-page); }
.composer {
  display: flex; gap: var(--space-2); align-items: flex-end;
  margin-top: var(--space-3); padding-top: var(--space-3);
  border-top: var(--border-thin) solid var(--color-border-on-surface);
}
.composer .field { flex: 1; }

/* ---- the five scores, showing their arithmetic (PRD 6) ----
   Collapsed by default: the record opens on the person, and the arithmetic is
   there for the coach who wants to argue with the number rather than being the
   first thing between them and the name. */
.scorelist { display: grid; }
.scorerow { border-top: var(--border-thin) solid var(--color-border-on-surface); }
.scorelist > .scorerow:first-child { border-top: 0; }
.scorerow > summary {
  display: flex; align-items: baseline; gap: var(--space-2);
  padding: var(--space-2) var(--space-1); cursor: pointer; list-style: none;
}
.scorerow > summary::-webkit-details-marker { display: none; }
.scorerow > summary::before {
  content: '›'; color: var(--color-text-placeholder);
  transition: transform .12s ease; flex: none;
}
.scorerow[open] > summary::before { transform: rotate(90deg); }
.scorerow .sname { flex: none; }
.scorerow .squestion { flex: 1; min-width: 0; }
.scorerow > summary .pill { flex: none; }
.scorerow .inputs { padding: 0 0 var(--space-3) var(--space-5); display: grid; gap: var(--space-2); }
.sinput { display: grid; gap: 3px; }
/* AN UNKNOWN INPUT IS QUIET, NOT ALARMING. It is not a failing of the member and
   must not read like one: no red, no zero, just the sentence naming what would
   fill it. */
.sinput.unknown .ilabel { color: var(--text-muted); }
.ibar {
  height: 4px; border-radius: var(--radius-full);
  background: var(--color-surface-page);
  border: var(--border-thin) solid var(--color-border-on-surface);
  overflow: hidden;
}
.ibar > i { display: block; height: 100%; background: var(--signal); }
.iwhy { color: var(--risk); }
.scorerow.nested { border-top: 0; }
.scorerow.nested > summary { padding-left: 0; }

/* ---- the Macros tab ---- */
/* Inputs in pairs: weight beside body fat, activity beside goal. One column on a phone. */
.macroform { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-3) var(--space-4); }
@media (max-width: 760px) { .macroform { grid-template-columns: 1fr; } }
.macrosave { display: grid; gap: var(--space-2); justify-items: start; margin-top: var(--space-4); }
/* The four figures on one line: they are read together, as one day. */
.macrostats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
@media (max-width: 1100px) { .macrostats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
